import { useState, useRef, useEffect, useCallback } from 'react';
import { API } from '@/App';
import {experienceJSON} from '../../utils/experienceRequest';
import {journey} from '../search/useDiscoverySearch';
// Memory only: never persist unsaved conversations or draft text in browser storage.
let suspended = null;
const headers = () => ({'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('session_token')}`});
const activeKey = () => `bds_ask_active_${(localStorage.getItem('session_token') || '').slice(-8)}`;
export const askRequest = async (path, init = {}) => {
  try {
    return await experienceJSON(`${API}/ask${path}`, {...init, deadlineMs:6000, headers:headers()});
  } catch(e) {if(e.code==='TIMEOUT')throw new Error('This request exceeded its response deadline. Retry; your question is preserved.');throw e;}
};

export const useAskConversation = (scopeKey='') => {
  const owner=`${scopeKey}:${localStorage.getItem('session_token') || ''}`;
  const [stateOwner,setStateOwner]=useState(null);
  const [id, setId] = useState(() => crypto.randomUUID());
  const [turns, setTurns] = useState([]), [context, setContext] = useState({});
  const [save, setSave] = useState(true), [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false), [status, setStatus] = useState({});
  const [error, setError] = useState(''), [historyError,setHistoryError]=useState('');
  const [startedAt,setStartedAt]=useState(null);
  const [draft,setDraft]=useState(''), [clearContext,setClearContext]=useState(false);
  const [reportDismissal,setReportDismissal]=useState(null);
  const snapshot=useRef(null);
  if(stateOwner===owner)snapshot.current={owner,id,turns,context,save,draft,clearContext,reportDismissal};
  const controller = useRef(), busyRef = useRef(false), mounted=useRef(true), sequence=useRef(0);
  const currentOwner=useRef(owner);currentOwner.current=owner;
  const loadRecent = useCallback(async () => {
    try {const rows=await askRequest('/conversations');if(mounted.current && currentOwner.current===owner){setRecent(Array.isArray(rows)?rows:[]);setHistoryError('');}}
    catch (e) {if(mounted.current && currentOwner.current===owner)setHistoryError(e.message);}
  }, [owner]);
  const open = useCallback(async cid => {
    if(busyRef.current)return;
    const token=++sequence.current;
    try {
      const doc = await askRequest(`/conversations/${cid}`);
      if(!mounted.current || currentOwner.current!==owner || token!==sequence.current || busyRef.current)return;
      setId(cid);setTurns(doc.turns || []);setContext(doc.context || {});setClearContext(false);setDraft('');setSave(true);setError('');
      sessionStorage.setItem(activeKey(), cid);
    } catch (e) {if(mounted.current && currentOwner.current===owner && token===sequence.current)setHistoryError(e.message);}
  }, [owner]);
  const hydratedOwner=useRef(null);
  useEffect(() => {
    mounted.current=true;controller.current?.abort();++sequence.current;busyRef.current=false;
    const previous=suspended?.owner===owner?suspended:null; suspended=null;
    if(hydratedOwner.current!==owner){
    hydratedOwner.current=owner;
    setStateOwner(owner);setId(previous?.id || crypto.randomUUID());setTurns(previous?.turns || []);setContext(previous?.context || {});setDraft(previous?.draft || '');setClearContext(previous?.clearContext || false);setReportDismissal(previous?.reportDismissal || null);setRecent([]);setBusy(false);setStatus({});setError('');setHistoryError('');setSave(previous?.save ?? true);
    }
    loadRecent();return()=>{
      if(snapshot.current?.owner===owner && currentOwner.current===owner) suspended={...snapshot.current,turns:snapshot.current.turns.map(t=>t.response?t:{...t,draft:'',response:{type:'tool_unavailable',message:'Response interrupted when you left. Retry your preserved question.'}})};
      else suspended=null;
      mounted.current=false;++sequence.current;controller.current?.abort();
    };
  }, [owner,loadRecent]);
  const reset = () => {
    if (busyRef.current) return;
    ++sequence.current;setId(crypto.randomUUID());setTurns([]);setContext({});setDraft('');setClearContext(false);setError('');setStatus({});sessionStorage.removeItem(activeKey());
  };
  const updateTurn = (rid, values) => {if(mounted.current)setTurns(prev => prev.map(t => t.request_id === rid ? {...t, ...values} : t));};
  const submit = async (query, retryId, selection, reportSubject) => {
    if (!query.trim() || busyRef.current || stateOwner!==owner) return;
    const execution=++sequence.current;
    const isCurrent=()=>mounted.current && currentOwner.current===owner && sequence.current===execution;
    const rid = retryId || crypto.randomUUID();
    const previous = retryId ? turns.find(t=>t.request_id===retryId) : null;
    const selectedEntity=selection || previous?.selectedEntity;
    const requestContext=previous?.requestContext || {context,report_subject:reportSubject || undefined,clear_context:clearContext};
    if(!retryId)journey('ask_question');
    busyRef.current=true;setBusy(true);setStartedAt(Date.now());setStatus({stage:'accepted',message:'Sending your question…'});setError('');
    if(retryId)updateTurn(rid,{response:null,draft:''});else setTurns(prev=>[...prev,{request_id:rid,userText:query,response:null,selectedEntity,requestContext}]);
    const abort=new AbortController();controller.current=abort;
    const timeout=setTimeout(()=>abort.abort('timeout'),115000);
    let completed=false;
    try {
      const res=await fetch(`${API}/ask/stream`,{method:'POST',headers:headers(),signal:abort.signal,
        body:JSON.stringify({query,conversation_id:id,request_id:rid,save,...requestContext,selected_entity:selectedEntity,journey_id:sessionStorage.getItem('bds_discovery_journey') || ''})});
      if(!res.ok){const data=await res.json().catch(()=>({}));throw new Error(res.status===401?'Sign in again. Your question is preserved.':typeof data.detail==='string'?data.detail:data.detail?.message || 'Ask could not complete this request.');}
      if(!res.body)throw new Error('Response streaming is unavailable. Retry your preserved question.');
      const reader=res.body.getReader(),decoder=new TextDecoder();let buffer='',draft='';
      while(true){
        const {value,done}=await reader.read();if(done)break;
        buffer+=decoder.decode(value,{stream:true}).replace(/\r\n/g,'\n');let boundary;
        while((boundary=buffer.indexOf('\n\n'))>=0){
          const block=buffer.slice(0,boundary);buffer=buffer.slice(boundary+2);
          const event=block.split('\n').find(l=>l.startsWith('event: '))?.slice(7);
          const payload=block.split('\n').filter(l=>l.startsWith('data: ')).map(l=>l.slice(6)).join('\n');if(!payload)continue;
          const data=JSON.parse(payload);
          if(event==='status' && isCurrent())setStatus(data);
          if(event==='heartbeat' && isCurrent())setStatus(previous=>({...previous,updated_at:data.updated_at}));
          if(event==='text' && isCurrent()){draft+=data.text;updateTurn(rid,{draft});}
          if(event==='result'){completed=true;if(isCurrent()){updateTurn(rid,{response:data,draft:''});setContext(data.context || {});setClearContext(false);if(data.saved)sessionStorage.setItem(activeKey(),id);}}
        }
        if(completed){await reader.cancel();break;}
      }
      if(!completed)throw new Error('The connection ended before an answer was ready. Retry this question.');
      if(save && isCurrent())loadRecent();
    } catch(e) {
      if(!completed && isCurrent()){const message=abort.signal.aborted ? (abort.signal.reason==='timeout'?'This response exceeded its deadline. Retry or open the report.':'Response cancelled. Your question is preserved.') : e.message;updateTurn(rid,{response:{type:'tool_unavailable',message,request_id:rid},draft:''});}
    } finally {clearTimeout(timeout);if(isCurrent()){busyRef.current=false;setBusy(false);setStatus({});}}
  };
  const remove=async cid=>{try{await askRequest(`/conversations/${cid}`,{method:'DELETE'});if(mounted.current && currentOwner.current===owner){if(cid===id)reset();loadRecent();}}catch(e){if(mounted.current && currentOwner.current===owner)setHistoryError(e.message);}};
  const visible=stateOwner===owner;
  return {id,reportDismissal,setReportDismissal,draft:visible?draft:'',setDraft,clearContext,removeContext:()=>{setContext({});setClearContext(true);},turns:visible?turns:[],context:visible?context:{},save,setSave,recent:visible?recent:[],busy:visible?busy:false,status:visible?status:{},error:visible?error:'',historyError:visible?historyError:'',startedAt,ready:visible,open,reset,submit,remove,loadRecent,cancel:()=>controller.current?.abort()};
};
